// Spectra utility functions — data detection, narrative generation, stat helpers

export type TableType = 'column' | 'xy' | 'paired' | 'unknown';
export type ColumnType = 'numeric' | 'text' | 'smiles';

export interface ParsedData {
  headers: string[];
  rows: (string | number)[][];
  detectedType: TableType;
  columnTypes: ColumnType[];
}

export interface DetectionBadge {
  type: TableType;
  label: string;
  detail: string;
}

/**
 * Detect delimiter from pasted text.
 */
export function detectDelimiter(text: string): string {
  const firstLine = text.split('\n')[0] || '';
  const tabCount = (firstLine.match(/\t/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;
  const semiCount = (firstLine.match(/;/g) || []).length;
  if (tabCount >= commaCount && tabCount >= semiCount) return '\t';
  if (commaCount >= semiCount) return ',';
  return ';';
}

/**
 * Detect if a string looks like a SMILES.
 */
function looksLikeSmiles(val: string): boolean {
  if (!val || val.length < 3) return false;
  // Contains common SMILES characters and no spaces
  return /^[A-Za-z0-9@+\-\[\]()=#/\\.%]+$/.test(val) &&
    /[A-Z]/.test(val) &&
    !(/^\d+\.?\d*$/.test(val));
}

/**
 * Detect column type from values.
 */
function detectColumnType(values: string[]): ColumnType {
  const nonEmpty = values.filter(v => v !== '' && v != null);
  if (nonEmpty.length === 0) return 'text';
  const numericCount = nonEmpty.filter(v => !isNaN(Number(v)) && v.trim() !== '').length;
  if (numericCount / nonEmpty.length > 0.8) return 'numeric';
  const smilesCount = nonEmpty.filter(v => looksLikeSmiles(String(v))).length;
  if (smilesCount / nonEmpty.length > 0.5) return 'smiles';
  return 'text';
}

/**
 * Parse clipboard text into structured data.
 */
export function parseClipboardData(text: string): ParsedData {
  const delimiter = detectDelimiter(text);
  const lines = text.trim().split('\n').map(l => l.split(delimiter));

  if (lines.length < 2) {
    return { headers: ['A'], rows: lines.map(l => l), detectedType: 'unknown', columnTypes: ['text'] };
  }

  // Check if first row is headers (mostly non-numeric)
  const firstRow = lines[0];
  const numericInFirst = firstRow.filter(v => !isNaN(Number(v)) && v.trim() !== '').length;
  const hasHeaders = numericInFirst / firstRow.length < 0.5;

  let headers: string[];
  let dataRows: string[][];

  if (hasHeaders) {
    headers = firstRow.map((h, i) => h.trim() || `Col ${i + 1}`);
    dataRows = lines.slice(1);
  } else {
    headers = firstRow.map((_, i) => `Col ${i + 1}`);
    dataRows = lines;
  }

  // Detect column types
  const columnTypes: ColumnType[] = headers.map((_, colIdx) => {
    const colValues = dataRows.map(row => row[colIdx] || '');
    return detectColumnType(colValues);
  });

  // Convert numeric cells
  const rows = dataRows.map(row =>
    row.map((cell, ci) => {
      if (columnTypes[ci] === 'numeric' && cell.trim() !== '' && !isNaN(Number(cell))) {
        return Number(cell);
      }
      return cell;
    })
  );

  const detectedType = detectTableType(headers, columnTypes, rows);

  return { headers, rows, detectedType, columnTypes };
}

/**
 * Detect table structure type.
 */
export function detectTableType(
  headers: string[],
  columnTypes: ColumnType[],
  rows: (string | number)[][]
): TableType {
  const numericCols = columnTypes.filter(t => t === 'numeric').length;

  if (numericCols === 0) return 'unknown';

  // XY: exactly 2 numeric columns
  if (columnTypes.length === 2 && numericCols === 2) return 'xy';

  // XY: first col numeric (X), second numeric (Y)
  if (columnTypes.length >= 2 && columnTypes[0] === 'numeric' && columnTypes[1] === 'numeric') {
    // Check if headers suggest XY (concentration/dose/time vs response/signal)
    const xyHeaders = /conc|dose|time|x|log|molar/i;
    const yHeaders = /response|signal|abs|od|y|viab|activity|rate|velocity/i;
    if (xyHeaders.test(headers[0]) || yHeaders.test(headers[1])) return 'xy';
  }

  // Paired: exactly 2 numeric columns with equal rows
  if (numericCols === 2 && columnTypes.length === 2) return 'paired';

  // Column: multiple numeric columns (groups)
  if (numericCols >= 2) return 'column';

  // Single column of numbers
  if (numericCols === 1) return 'column';

  return 'unknown';
}

/**
 * Get detection badge info.
 */
export function getDetectionBadge(
  type: TableType,
  headers: string[],
  columnTypes: ColumnType[]
): DetectionBadge {
  const numericCols = columnTypes.filter(t => t === 'numeric').length;
  switch (type) {
    case 'xy':
      return { type, label: 'XY', detail: `${headers[0]} vs ${headers[1]}` };
    case 'column':
      return { type, label: 'Column', detail: `${numericCols} group${numericCols !== 1 ? 's' : ''}` };
    case 'paired':
      return { type, label: 'Paired', detail: `${headers[0]} vs ${headers[1]}` };
    default:
      return { type, label: 'Unknown', detail: 'Paste numeric data to begin' };
  }
}

/**
 * Get significance label from p-value.
 */
export function getSignificanceLabel(pValue: number): string {
  if (pValue < 0.001) return '***';
  if (pValue < 0.01) return '**';
  if (pValue < 0.05) return '*';
  return 'ns';
}

/**
 * Compute Cohen's d client-side for quick preview.
 */
export function computeCohenD(group1: number[], group2: number[]): { d: number; interpretation: string } {
  const n1 = group1.length, n2 = group2.length;
  const m1 = group1.reduce((a, b) => a + b, 0) / n1;
  const m2 = group2.reduce((a, b) => a + b, 0) / n2;
  const s1 = Math.sqrt(group1.reduce((a, b) => a + (b - m1) ** 2, 0) / (n1 - 1));
  const s2 = Math.sqrt(group2.reduce((a, b) => a + (b - m2) ** 2, 0) / (n2 - 1));
  const pooled = Math.sqrt(((n1 - 1) * s1 ** 2 + (n2 - 1) * s2 ** 2) / (n1 + n2 - 2));
  const d = pooled === 0 ? 0 : (m1 - m2) / pooled;
  const ad = Math.abs(d);
  const interpretation = ad < 0.2 ? 'negligible' : ad < 0.5 ? 'small' : ad < 0.8 ? 'medium' : 'large';
  return { d: Math.round(d * 1000) / 1000, interpretation };
}

/**
 * Extract numeric columns from parsed data as groups.
 */
export function extractGroups(data: ParsedData): number[][] {
  const groups: number[][] = [];
  data.columnTypes.forEach((type, colIdx) => {
    if (type === 'numeric') {
      const values = data.rows
        .map(row => row[colIdx])
        .filter(v => v !== '' && v != null && !isNaN(Number(v)))
        .map(Number);
      if (values.length > 0) groups.push(values);
    }
  });
  return groups;
}

/**
 * Extract X and Y arrays from parsed data.
 */
export function extractXY(data: ParsedData): { x: number[]; y: number[] } | null {
  const numericIndices = data.columnTypes
    .map((t, i) => t === 'numeric' ? i : -1)
    .filter(i => i >= 0);
  if (numericIndices.length < 2) return null;
  const xi = numericIndices[0], yi = numericIndices[1];
  const x: number[] = [], y: number[] = [];
  data.rows.forEach(row => {
    const xv = Number(row[xi]), yv = Number(row[yi]);
    if (!isNaN(xv) && !isNaN(yv)) {
      x.push(xv);
      y.push(yv);
    }
  });
  return x.length > 0 ? { x, y } : null;
}

/**
 * Generate formatted narrative for journal copy.
 */
export function generateCopyText(result: any): string {
  if (!result) return '';
  const lines: string[] = [result.test_name || 'Statistical Analysis'];
  lines.push('');
  if (result.group_stats) {
    result.group_stats.forEach((gs: any, i: number) => {
      lines.push(`Group ${i + 1}: M = ${gs.mean}, SD = ${gs.std}, n = ${gs.n}`);
    });
    lines.push('');
  }
  if (result.statistic != null) lines.push(`Test statistic: ${result.statistic}`);
  if (result.p_value != null) lines.push(`p-value: ${result.p_value}`);
  if (result.effect_size != null) lines.push(`Effect size: ${result.effect_size} (${result.effect_label || ''})`);
  if (result.r_value != null) lines.push(`r = ${result.r_value}, R\u00B2 = ${result.r_squared}`);
  if (result.slope != null) lines.push(`Slope: ${result.slope}, Intercept: ${result.intercept}`);
  if (result.ci_95) lines.push(`95% CI: [${result.ci_95[0]}, ${result.ci_95[1]}]`);
  lines.push('');
  if (result.narrative) lines.push(result.narrative);
  return lines.join('\n');
}
